import { apiClient } from "@apis/apiClient";
import type {
  GetEmbeddedDashboardParams,
  GetDashboardsForMenuParams,
  GetOrganisationsWithCustomReportsParams,
  GetCustomReportsByOrganisationParams,
  CreateDashboardParams,
  UpdateDashboardParams,
  FavoriteDashboardParams,
  TestBIConnectionParams,
  SearchDashboardsParams,
  BIDashboard,
  DashboardMenuItem,
  OrganizationWithCustomReports,
  CustomReport,
  CloneDashboardInfo,
  EmbeddedDashboard,
  ApiResponse,
} from "./businessIntelligenceClient.dto";

/**
 * Business Intelligence API Client
 *
 * Provides methods for managing Power BI dashboards and custom reports,
 * including embedding, searching, cloning, and synchronization.
 * Uses DTOs from businessIntelligenceClient.dto.ts for type safety.
 */
const businessIntelligenceClient = {
  /**
   * Get embedded dashboard with access token
   * @param params - Dashboard embed parameters
   * @returns Embedded dashboard with token
   */
  getEmbeddedDashboard(
    params: GetEmbeddedDashboardParams
  ): Promise<ApiResponse<EmbeddedDashboard>> {
    return apiClient.get(`/rest/bi/dashboard/embed/${params.id}`, {
      params: params,
    });
  },

  /**
   * Get dashboards for menu display
   * @param params - Menu filter parameters
   * @returns Array of dashboard menu items
   */
  getDashBoardsForMenu(
    params: GetDashboardsForMenuParams
  ): Promise<ApiResponse<DashboardMenuItem[]>> {
    return apiClient.get(`/rest/bi/dashboard/menu`, { params: params });
  },

  /**
   * Get organizations that have custom reports
   * @param params - Workspace parameters
   * @returns Array of organizations with custom reports
   */
  getOrganisationsWithCustomReports(
    params: GetOrganisationsWithCustomReportsParams
  ): Promise<ApiResponse<OrganizationWithCustomReports[]>> {
    return apiClient.get(`/rest/bi/powerbi/workspace/${params.id}/orgs`);
  },

  /**
   * Get custom reports by organization
   * @param params - Organization and workspace parameters
   * @returns Array of custom reports
   */
  getCustomReportsByOrganisation(
    params: GetCustomReportsByOrganisationParams
  ): Promise<ApiResponse<CustomReport[]>> {
    return apiClient.get(`/rest/bi/powerbi/workspace/${params.id}/reports`, {
      params: params,
    });
  },

  /**
   * Get dashboard information for cloning
   * @param id - Dashboard ID
   * @returns Clone dashboard information
   */
  getCloneDashboardInfo(id: string): Promise<ApiResponse<CloneDashboardInfo>> {
    return apiClient.get(`/rest/bi/dashboard/clone/${id}`);
  },

  /**
   * Get a specific dashboard by ID
   * @param id - Dashboard ID
   * @returns Dashboard details
   */
  getDashboard(id: string): Promise<ApiResponse<BIDashboard>> {
    return apiClient.get(`/rest/bi/dashboard/${id}`);
  },

  /**
   * Create a new BI dashboard
   * @param params - Dashboard creation parameters
   * @returns Created dashboard
   */
  create(params: CreateDashboardParams): Promise<ApiResponse<BIDashboard>> {
    return apiClient.post(`/rest/bi`, params);
  },

  /**
   * Update an existing dashboard
   * @param id - Dashboard ID
   * @param params - Dashboard update parameters
   * @returns Updated dashboard
   */
  update(
    id: string,
    params: UpdateDashboardParams
  ): Promise<ApiResponse<BIDashboard>> {
    return apiClient.put(`/rest/bi/${id}`, params);
  },

  /**
   * Change dashboard image
   * @param id - Dashboard ID
   * @param nameFile - Image file name
   * @returns Update response
   */
  changeImage(id: string, nameFile: string): Promise<ApiResponse<BIDashboard>> {
    return apiClient.put(`/rest/bi/changeImage/${id}/?image=${nameFile}`);
  },

  /**
   * Mark dashboard as favorite or unfavorite
   * @param params - Favorite parameters
   * @returns Updated dashboard
   */
  favorite(params: FavoriteDashboardParams): Promise<ApiResponse<BIDashboard>> {
    return apiClient.put(`/rest/bi/favorite`, params);
  },

  /**
   * Delete a dashboard
   * @param id - Dashboard ID
   * @returns Deletion response
   */
  delete(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/rest/bi/${id}`);
  },

  /**
   * Test BI connection configuration
   * @param params - Test connection parameters
   * @returns Test result
   */
  test(
    params: TestBIConnectionParams
  ): Promise<ApiResponse<{ success: boolean; message?: string }>> {
    return apiClient.post(`/rest/bi/test`, params);
  },

  /**
   * Activate or deactivate a dashboard
   * @param id - Dashboard ID
   * @param active - Active status
   * @returns Updated dashboard
   */
  activate(id: string, active: boolean): Promise<ApiResponse<BIDashboard>> {
    return apiClient.put(`/rest/bi/dashboard/active/${id}/${active}`);
  },

  /**
   * Synchronize Power BI workspace
   * @param id - Workspace ID
   * @returns Sync response
   */
  sync(id: string): Promise<ApiResponse<{ syncedCount: number }>> {
    return apiClient.post(`/rest/bi/powerbi/workspace/sync/${id}`);
  },

  /**
   * Search dashboards with filters
   * @param params - Search parameters
   * @returns Array of matching dashboards
   */
  search(params: SearchDashboardsParams): Promise<ApiResponse<BIDashboard[]>> {
    return apiClient.post(`/rest/bi/search`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  /**
   * Search dashboards for partner (all organizations)
   * @param params - Search parameters
   * @returns Array of matching dashboards
   */
  searchForDashboardPartner(
    params: SearchDashboardsParams
  ): Promise<ApiResponse<BIDashboard[]>> {
    return apiClient.post(`/rest/bi/search/partner/all/dashboard`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  /**
   * Search all dashboards
   * @param params - Search parameters
   * @returns Array of matching dashboards
   */
  searchAllDashboard(
    params: SearchDashboardsParams
  ): Promise<ApiResponse<BIDashboard[]>> {
    return apiClient.post(`/rest/bi/search/all/dashboard`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },
};

export default businessIntelligenceClient;
